import { apiService, type IAPIResponse } from '@/services/api'
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { ActivatedLevyDto, CompanyRegistrationResponse, CompanySubscription } from './member'

export interface ResetPasswordRequest {
  token: string
  newPassword: string
}

export interface ValidateResetTokenRequest {
  token: string
}

export interface LoginRequest {
  username: string
  password: string
  terminalCode: string
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
  locationId?: number
}

export interface ChangePasswordRequest {
  currentPassword: string
  newPassword: string
}

export interface LoginResponse {
  token: string
  refreshToken: string
  expiresAt: string
  user: AuthUser
}

export interface AuthResponse {
  token: string
  user: AuthUser
  terminal?: TerminalInfo
}

export interface AuthUser {
  userId: number
  username: string
  firstName: string
  lastName: string
  email: string
  role: string
}

export interface TerminalInfo {
  companyCode: string
  terminalCode: string
  expiryDate: string
  terminalSite?: TerminalSite
  isVatRegistered: boolean
  isSageEnabled: boolean
  activatedLevies?: ActivatedLevyDto[]
}

export interface TokenResponse {
  token: string
  refreshToken: string
  expiresAt: string
}

export interface TerminalSite {
  siteName: string
  siteId: string
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const router = useRouter()

    const user = ref<AuthUser | null>(null)
    const token = ref<string | null>(null)
    const refreshToken = ref<string | null>(null)
    const configuration = ref<string | null>(null)
    const isEisvvatRegistered = ref<boolean>(false)
    const activatedLevyDtos = ref<ActivatedLevyDto[]>([])
    const terminalSite = ref<TerminalSite | null>(null)
    const loading = ref(false)
    const error = ref<string | null>(null)
    const isLoggingOut = ref(false)
    const _hydrated = ref(false)
    const isSageEnabled = ref(false)

    watch(token, (newVal, oldVal) => {
      if (newVal === null && oldVal !== null) {
        console.trace('⚠️ TOKEN WAS CLEARED - call stack:')
      }
      if (newVal !== null && oldVal === null) {
        console.log('✅ TOKEN WAS SET')
      }
    })
    const isAuthenticated = computed(() => !!token.value && !!user.value)

    const _markHydrated = () => {
      _hydrated.value = true
    }

    // Actions
    const login = async (credentials: LoginRequest): Promise<boolean> => {
      loading.value = true
      error.value = null
      isLoggingOut.value = false

      try {
        const response = await apiService.post<IAPIResponse<AuthResponse>>(
          '/auth/login',
          credentials,
        )

        console.log('The login response is: ' + JSON.stringify(response))
        if (response.isSuccessful && response.payload) {
          // Set all values - Pinia persist plugin will handle localStorage automatically
          token.value = response.payload.token
          user.value = response.payload.user
          isEisvvatRegistered.value = response.payload.terminal?.isVatRegistered || false
          activatedLevyDtos.value = response.payload.terminal?.activatedLevies || []
          terminalSite.value = response.payload.terminal?.terminalSite || null
          isSageEnabled.value = response.payload.terminal?.isSageEnabled || false
          return true
        } else {
          error.value = response.remark || 'Login failed'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Login failed'
        return false
      } finally {
        loading.value = false
      }
    }

    const register = async (userData: RegisterRequest): Promise<boolean> => {
      loading.value = true
      error.value = null

      try {
        const response = await apiService.post<IAPIResponse<AuthUser>>('/Auth/register', userData)

        if (response.isSuccessful) {
          return true
        } else {
          error.value = response.remark || 'Registration failed'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Registration failed'
        return false
      } finally {
        loading.value = false
      }
    }

    const logout = async (skipNavigation: boolean = false): Promise<void> => {
      if (isLoggingOut.value) {
        return
      }

      isLoggingOut.value = true
      loading.value = true

      try {
        if (token.value) {
          try {
            await apiService.post<IAPIResponse<boolean>>('/Auth/logout', {})
          } catch (err) {}
        }
      } finally {
        // Clear all auth data - Pinia persist will clear localStorage automatically
        user.value = null
        token.value = null
        refreshToken.value = null
        configuration.value = null
        isEisvvatRegistered.value = false
        activatedLevyDtos.value = []
        terminalSite.value = null
        isSageEnabled.value = false
        loading.value = false
        isLoggingOut.value = false

        if (!skipNavigation) {
          await router.push({ name: 'login' })
        }
      }
    }

    const validateResetToken = async (tokenValue: string): Promise<boolean> => {
      if (!tokenValue) return false

      loading.value = true
      error.value = null

      try {
        const response = await apiService.post<IAPIResponse<boolean>>(
          '/auth/validate-reset-token',
          { token: tokenValue },
        )

        return response.isSuccessful
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Token validation failed'
        return false
      } finally {
        loading.value = false
      }
    }

    const resetPassword = async (passwordData: ResetPasswordRequest): Promise<boolean> => {
      loading.value = true
      error.value = null

      try {
        const response = await apiService.post<IAPIResponse<object>>(
          '/Auth/reset-password',
          passwordData,
        )

        if (response.isSuccessful) {
          return true
        } else {
          error.value = response.remark || 'Password reset failed'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Password reset failed'
        return false
      } finally {
        loading.value = false
      }
    }

    const getCurrentUser = async (): Promise<boolean> => {
      if (!token.value) {
        return false
      }

      try {
        const response = await apiService.get<IAPIResponse<AuthUser>>('/Auth/me')

        if (response.isSuccessful && response.payload) {
          user.value = response.payload
          return true
        } else {
          return false
        }
      } catch (err) {
        return false
      }
    }

    const changePassword = async (passwordData: ChangePasswordRequest): Promise<boolean> => {
      loading.value = true
      error.value = null

      try {
        const response = await apiService.post<IAPIResponse<object>>(
          '/Auth/change-password',
          passwordData,
        )

        if (response.isSuccessful) {
          return true
        } else {
          error.value = response.remark || 'Password change failed'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Password change failed'
        return false
      } finally {
        loading.value = false
      }
    }

    const forgotPassword = async (email: string): Promise<boolean> => {
      loading.value = true
      error.value = null

      try {
        const response = await apiService.post<IAPIResponse<object>>('/Auth/forgot-password', {
          email,
        })

        if (response.isSuccessful) {
          return true
        } else {
          error.value = response.remark || 'Failed to send reset email'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to send reset email'
        return false
      } finally {
        loading.value = false
      }
    }
    const refreshVATStatus = async (): Promise<boolean> => {
      if (!token.value) {
        return false
      }

      loading.value = true
      error.value = null

      try {
        const response = await apiService.get<
          IAPIResponse<{
            isVatRegistered: boolean
            token: string
            hasChanged: boolean
          }>
        >('/Auth/vat-status')

        if (response.isSuccessful && response.payload) {
          const { isVatRegistered, token: newToken, hasChanged } = response.payload

          // Update VAT status
          isEisvvatRegistered.value = isVatRegistered

          // Update token with the new one containing updated claims
          if (newToken) {
            token.value = newToken
          }

          // Optional: Log if status changed
          if (hasChanged) {
            console.log('VAT registration status changed:', isVatRegistered)
          }

          return true
        } else {
          error.value = response.remark || 'Failed to fetch VAT status'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to fetch VAT status'
        return false
      } finally {
        loading.value = false
      }
    }

    const registerCompany = async (companyData: CompanySubscription): Promise<boolean> => {
      loading.value = true
      error.value = null

      try {
        const response = await apiService.post<IAPIResponse<CompanyRegistrationResponse>>(
          '/Company/register-company',
          companyData,
        )

        if (response.isSuccessful && response.payload) {
          return true
        } else {
          error.value = response.remark || 'Company registration failed'
          return false
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Company registration failed'
        return false
      } finally {
        loading.value = false
      }
    }

    const clearError = (): void => {
      error.value = null
    }

    // Computed getters
    const userFullName = computed(() => {
      if (!user.value) return ''
      return `${user.value.firstName} ${user.value.lastName}`
    })

    return {
      // State
      user,
      token,
      loading,
      error,
      isAuthenticated,
      isEisvvatRegistered,
      configuration,
      activatedLevyDtos,
      terminalSite,
      _hydrated,
      isSageEnabled,

      userFullName,

      login,
      register,
      logout,
      getCurrentUser,
      changePassword,
      forgotPassword,
      clearError,
      validateResetToken,
      resetPassword,
      registerCompany,
      _markHydrated,
      refreshVATStatus,
    }
  },
  {
    persist: {
      storage: localStorage,
      pick: [
        'user',
        'token',
        'refreshToken',
        'configuration',
        'isEisvvatRegistered',
        'activatedLevyDtos',
        'terminalSite',
        'isSageEnabled',
      ],
    },
  },
)
